import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import socket from '../socket';
import BattleSongCard from '../components/BattleSongCard';
import Bracket from '../components/Bracket';

function Battle() {
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const [bracket, setBracket] = useState(null);
  const [currentMatch, setCurrentMatch] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [voted, setVoted] = useState(false);
  const [myVote, setMyVote] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [champion, setChampion] = useState(null);
  const [showBracket, setShowBracket] = useState(false);
  const [roundName, setRoundName] = useState('');
  const [roundProgress, setRoundProgress] = useState('');
  const [isTieBreaker, setIsTieBreaker] = useState(false);
  const [votingDuration, setVotingDuration] = useState(30);
  const [isHost, setIsHost] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);

  const isPresenter = isHost && presentationMode;

  useEffect(() => {
    if (socket.connected) {
      socket.emit('get-battle-state', { roomCode }, (response) => {
        if (response.success) {
          setBracket(response.bracket);
          if (response.currentMatch) setCurrentMatch(response.currentMatch);
          if (typeof response.host !== 'undefined') setIsHost(response.host === socket.id);
          if (typeof response.presentationMode !== 'undefined') setPresentationMode(response.presentationMode);
        }
      });
    }

    socket.on('tournament-started', ({ bracket: b, currentMatch: m, host, presentationMode: pm }) => {
      setBracket(b);
      setCurrentMatch(m);
      if (typeof host !== 'undefined') setIsHost(host === socket.id);
      if (typeof pm !== 'undefined') setPresentationMode(pm);
    });

    socket.on('voting-start', ({ match, timeLeft: t, roundName: rn, roundProgress: rp, isTieBreaker: tb, votingDuration: vd }) => {
      setCurrentMatch(match);
      setTimeLeft(t);
      setVoted(false);
      setMyVote(null);
      setLastResult(null);
      if (rn) setRoundName(rn);
      if (rp) setRoundProgress(rp);
      setIsTieBreaker(!!tb);
      if (vd) setVotingDuration(vd);
    });

    socket.on('voting-timer', ({ timeLeft: t }) => {
      setTimeLeft(t);
    });

    socket.on('vote-update', ({ matchId, votes, voterCount }) => {
      setCurrentMatch(prev => {
        if (prev && prev.id === matchId) {
          return { ...prev, votes, voterCount };
        }
        return prev;
      });
    });

    socket.on('match-result', ({ matchId, winner, winnerSong, votes }) => {
      setLastResult({ winner, winnerSong, votes });
      setCurrentMatch(null);
    });

    socket.on('bracket-updated', ({ bracket: b }) => {
      setBracket(b);
    });

    socket.on('tournament-finished', ({ champion: c, bracket: b }) => {
      setChampion(c);
      setBracket(b);
      confetti({ particleCount: 200, spread: 100, origin: { y: 0.6 } });
      setTimeout(() => {
        confetti({ particleCount: 100, spread: 70, origin: { x: 0.3, y: 0.5 } });
        confetti({ particleCount: 100, spread: 70, origin: { x: 0.7, y: 0.5 } });
      }, 500);
    });

    socket.on('kicked', () => {
      navigate('/', { replace: true });
    });

    return () => {
      socket.off('tournament-started');
      socket.off('voting-start');
      socket.off('voting-timer');
      socket.off('vote-update');
      socket.off('match-result');
      socket.off('bracket-updated');
      socket.off('tournament-finished');
      socket.off('kicked');
    };
  }, [navigate, roomCode]);

  const handleVote = (choice) => {
    if (voted || !currentMatch) return;
    socket.emit('vote', { roomCode, matchId: currentMatch.id, choice }, (response) => {
      if (response.success) {
        setVoted(true);
        setMyVote(choice);
      }
    });
  };

  const handleNextRound = () => {
    if (!currentMatch) return;
    socket.emit('skip-voting', { roomCode, matchId: currentMatch.id }, () => {});
  };

  // Champion screen
  if (champion) {
    return (
      <div className="battle-page champion-screen">
        <div className="champion-container">
          <h1 className="champion-title">🏆 Gewinner! 🏆</h1>
          <div className="champion-song">
            <img src={champion.albumCover} alt={champion.name} className="champion-cover" />
            <h2>{champion.name}</h2>
            <p>{champion.artist}</p>
            {champion.submittedBy && (
              <p className="champion-submitter">Eingereicht von <strong>{champion.submittedBy}</strong></p>
            )}
            <iframe
              src={`https://open.spotify.com/embed/track/${champion.id}?utm_source=generator&theme=0`}
              width="100%" height="152" frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy" title="Winner" style={{ borderRadius: 12, marginTop: 16 }}
            />
          </div>
          <div className="champion-actions">
            <button className="btn btn-primary btn-large" onClick={() => setShowBracket(!showBracket)}>
              {showBracket ? 'Gewinner' : '📊 Turnierbaum'}
            </button>
            <button className="btn btn-ghost" onClick={() => navigate('/')}>
              Zurück zum Start
            </button>
          </div>
          {showBracket && bracket && (
            <div className="champion-bracket">
              <Bracket bracket={bracket} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Result intermission
  if (lastResult && !currentMatch) {
    return (
      <div className="battle-page result-screen">
        <div className="result-container">
          <h2>Gewinner dieser Runde</h2>
          <div className="result-song">
            <img src={lastResult.winnerSong.albumCover} alt={lastResult.winnerSong.name} className="result-cover" />
            <h3>{lastResult.winnerSong.name}</h3>
            <p>{lastResult.winnerSong.artist}</p>
          </div>
          <div className="result-votes">
            <span>{lastResult.votes.a} vs {lastResult.votes.b}</span>
          </div>
          {lastResult.winnerSong?.submittedBy && (
            <p className="result-submitter">Eingereicht von <strong>{lastResult.winnerSong.submittedBy}</strong></p>
          )}
          <p className="waiting-text">Nächste Runde startet gleich...</p>
        </div>
      </div>
    );
  }

  // Voting screen
  if (currentMatch) {
    const timerMax = isTieBreaker ? 10 : (votingDuration === 0 ? null : votingDuration);
    const totalVotes = (currentMatch.votes?.a || 0) + (currentMatch.votes?.b || 0);

    return (
      <div className={`battle-page voting-screen ${isPresenter ? 'presenter-mode' : ''}`}>
        <div className="battle-header">
          {roundName && <div className="round-label">
            {isTieBreaker ? '⚡ Gleichstand! Revote!' : `${roundName}${roundProgress ? ` ${roundProgress}` : ''}`}
          </div>}
          {timerMax ? (
            <div className="timer">
              <div className="timer-bar" style={{ width: `${(timeLeft / timerMax) * 100}%` }}></div>
              <span className="timer-text">{timeLeft}s</span>
            </div>
          ) : (
            <div className="timer timer-unlimited">
              <span className="timer-text">∞</span>
            </div>
          )}
          <button className="btn btn-ghost btn-small" onClick={() => setShowBracket(!showBracket)}>
            {showBracket ? 'Battle' : '📊 Bracket'}
          </button>
          {isHost && !showBracket && (
            <button className="btn btn-primary btn-small next-round-btn" onClick={handleNextRound}>
              ⏭️ Nächste Runde
            </button>
          )}
        </div>

        {isPresenter && !showBracket && (
          <div className="presenter-banner">
            📺 Präsentationsmodus • {totalVotes} {totalVotes === 1 ? 'Stimme' : 'Stimmen'} abgegeben
          </div>
        )}

        {showBracket && bracket ? (
          <Bracket bracket={bracket} />
        ) : (
          <div className="matchup">
            <BattleSongCard
              song={currentMatch.songA}
              submitter={currentMatch.songA?.submittedBy}
              votes={currentMatch.votes?.a || 0}
              totalVotes={totalVotes}
              voted={voted}
              isMyVote={myVote === 'a'}
              onVote={() => handleVote('a')}
              presenter={isPresenter}
            />

            <div className="vs-divider">
              <span>VS</span>
            </div>

            <BattleSongCard
              song={currentMatch.songB}
              submitter={currentMatch.songB?.submittedBy}
              votes={currentMatch.votes?.b || 0}
              totalVotes={totalVotes}
              voted={voted}
              isMyVote={myVote === 'b'}
              onVote={() => handleVote('b')}
              presenter={isPresenter}
            />
          </div>
        )}

        {voted && !isPresenter && (
          <div className="voted-message">
            ✓ Abgestimmt! Warte auf das Ergebnis...
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="battle-page">
      <div className="loading-page">
        <div className="spinner"></div>
        <p>Turnier wird geladen...</p>
      </div>
    </div>
  );
}

export default Battle;
